import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

// Modal Nova Transação - Fixed Income
export function NovaTransacaoFixedIncomeModal({ onClose, onSuccess, fixedIncomeTipos, editingTransaction }) {
  const [tipoInvestimento, setTipoInvestimento] = useState(editingTransaction?.tipo_investimento || '');
  const [showNewTipo, setShowNewTipo] = useState(false);
  const [novoTipo, setNovoTipo] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const data = {
      tipo_investimento: formData.get('tipo_investimento'),
      data_aplicacao: formData.get('data_aplicacao'),
      dias_vencimento: formData.get('dias_vencimento') ? parseInt(formData.get('dias_vencimento')) : null,
      valor_investido: parseFloat(formData.get('valor_investido')),
      rentabilidade: formData.get('rentabilidade') ? parseFloat(formData.get('rentabilidade')) : null
    };

    try {
      if (editingTransaction) {
        await axios.put(`${API}/fixed-income/transacoes/${editingTransaction.id}`, data, getAuthHeaders());
        toast.success('Transação atualizada com sucesso');
      } else {
        await axios.post(`${API}/fixed-income/transacoes`, data, getAuthHeaders());
        toast.success('Transação criada com sucesso');
      }
      onSuccess();
    } catch (error) {
      toast.error('Erro ao salvar transação');
      console.error(error);
    }
  };

  const handleAddNovoTipo = async () => {
    if (!novoTipo.trim()) return;

    try {
      await axios.post(`${API}/fixed-income/tipos`, { nome: novoTipo }, getAuthHeaders());
      toast.success('Tipo adicionado');
      setTipoInvestimento(novoTipo);
      setShowNewTipo(false);
      setNovoTipo('');
      onSuccess(); // Recarregar para atualizar lista
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Tipo já existe');
      } else {
        toast.error('Erro ao adicionar tipo');
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-gray-900">
            {editingTransaction ? 'Editar' : 'Nova'} Transação - Fixed Income
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* Tipo de Investimento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Investimento *
              </label>
              {showNewTipo ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={novoTipo}
                    onChange={(e) => setNovoTipo(e.target.value)}
                    placeholder="Digite o novo tipo"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddNovoTipo}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Adicionar
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowNewTipo(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Cancelar
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    list="tipos-investimento"
                    name="tipo_investimento"
                    value={tipoInvestimento}
                    onChange={(e) => setTipoInvestimento(e.target.value)}
                    required
                    placeholder="Selecione ou digite um tipo"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <datalist id="tipos-investimento">
                    {fixedIncomeTipos.map((tipo) => (
                      <option key={tipo.id} value={tipo.nome} />
                    ))}
                  </datalist>
                  <button
                    type="button"
                    onClick={() => setShowNewTipo(true)}
                    className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                    title="Adicionar novo tipo"
                  >
                    +
                  </button>
                </div>
              )}
            </div>

            {/* Data da Aplicação */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data da Aplicação *
              </label>
              <input
                type="date"
                name="data_aplicacao"
                required
                defaultValue={editingTransaction?.data_aplicacao}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Dias de Vencimento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dias de Vencimento
              </label>
              <input
                type="number"
                name="dias_vencimento"
                min="0"
                defaultValue={editingTransaction?.dias_vencimento || ''}
                placeholder="Ex: 30"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Valor Investido */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Valor Investido ($) *
              </label>
              <input
                type="number"
                name="valor_investido"
                step="0.01"
                min="0"
                required
                defaultValue={editingTransaction?.valor_investido}
                placeholder="Ex: 1000.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Rentabilidade */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rentabilidade (% a.a.)
              </label>
              <input
                type="number"
                name="rentabilidade"
                step="0.01"
                defaultValue={editingTransaction?.rentabilidade || ''}
                placeholder="Ex: 12.5"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex gap-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
            >
              {editingTransaction ? 'Atualizar' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Modal Gerenciar Tipos de Investimento
export function GerenciarTiposFixedIncomeModal({ onClose, onSuccess, fixedIncomeTipos }) {
  const [novoTipo, setNovoTipo] = useState('');

  const handleAddTipo = async () => {
    if (!novoTipo.trim()) return;

    try {
      await axios.post(`${API}/fixed-income/tipos`, { nome: novoTipo }, getAuthHeaders());
      toast.success('Tipo adicionado');
      setNovoTipo('');
      onSuccess();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Tipo já existe');
      } else {
        toast.error('Erro ao adicionar tipo');
      }
    }
  };

  const handleDeleteTipo = async (tipoId) => {
    if (!window.confirm('Deseja excluir este tipo? (Não afetará transações existentes)')) return;

    try {
      await axios.delete(`${API}/fixed-income/tipos/${tipoId}`, getAuthHeaders());
      toast.success('Tipo excluído');
      onSuccess();
    } catch (error) {
      toast.error('Erro ao excluir tipo');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Gerenciar Tipos de Investimento</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          <div className="flex gap-2 mb-6">
            <input
              type="text"
              value={novoTipo}
              onChange={(e) => setNovoTipo(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddTipo()}
              placeholder="Digite o nome do novo tipo"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleAddTipo}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
            >
              Adicionar
            </button>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {fixedIncomeTipos.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Nenhum tipo cadastrado</p>
            ) : (
              fixedIncomeTipos.map((tipo) => (
                <div
                  key={tipo.id}
                  className="flex justify-between items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                >
                  <span className="font-medium text-gray-900">{tipo.nome}</span>
                  <button
                    onClick={() => handleDeleteTipo(tipo.id)}
                    className="text-red-600 hover:text-red-800 text-sm px-3 py-1 rounded hover:bg-red-50"
                  >
                    Excluir
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
